import { Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon } from '@persona-id/design-tokens/icons';
import { castArray } from 'lodash/fp';
import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import DynamicInput from 'dashboard/components/DynamicInput/DynamicInput';
import { intersperse } from 'lib/utils/array';

const Container = styled.nav`
  display: flex;
  align-items: center;
  margin-right: ${Spacing[2]};
  padding-left: ${Spacing[2]};
  overflow: hidden;
`;

const linkStyling = css`
  display: flex;
  align-items: center;
  white-space: nowrap;
  height: ${Spacing[5]};
  padding: ${Spacing[1]};
`;

const BackButton = styled.button`
  ${linkStyling}
  margin-left: -${Spacing[3]};
  background: none;
  cursor: pointer;

  svg {
    transform: translateX(2px);
  }

  &:hover,
  &:focus {
    svg {
      transform: translateX(-2px);
    }
  }
`;

const NavBackIcon = styled(ArrowLeftIcon)`
  flex: none;
  transition: transform 0.2s;
  height: ${Spacing[4]};
  width: ${Spacing[4]};

  rect[stroke],
  path[stroke] {
    stroke: ${({ theme }) => theme.color.text.primary};
  }
`;

const InputWrapper = styled.div`
  span {
    padding-right: 10px;
  }
`;

const Separator = styled.span`
  padding-left: ${Spacing[1]};
  padding-right: ${Spacing[1]};
`;

const BreadcrumbLink = styled(Link)`
  ${linkStyling}
  cursor: pointer;
`;

const BreadcrumbSecondary = styled.div`
  ${linkStyling}
  cursor: none;
  pointer-events: none;
  span {
    color: ${({ theme }) => theme.color.text.secondary};
    font-weight: 500;
  }
`;

const Label = styled.span`
  display: inline-block;
  max-width: 550px;
  color: ${({ theme }) => theme.color.text.tertiary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface BreadcrumbProps {
  displayName: string;
  to?: string;
}

interface NavBreadcrumbProps {
  name: string;
  breadcrumbItems: BreadcrumbProps[];
  className?: string;
  prefixElement?: ReactNode;
  suffixElement?: ReactNode;
  separator?: string;
  editable?: boolean;
  hidden?: boolean;
  onBackClick: () => void;
  onNameUpdate?: (name: string) => void;
}

const NavBreadcrumb = ({
  name,
  breadcrumbItems,
  className,
  prefixElement = <NavBackIcon />,
  suffixElement,
  separator = '/',
  hidden = false,
  editable,
  onBackClick,
  onNameUpdate,
}: NavBreadcrumbProps) => {
  const [templateName, setTemplateName] = useState(name);

  useEffect(() => {
    setTemplateName(name);
  }, [name]);

  if (hidden) {
    return null;
  }

  const breadcrumbs = [
    <InputWrapper key='name-breadcrumb__initial'>
      <DynamicInput
        aria-label='Edit template name'
        value={templateName}
        disabled={!editable || onNameUpdate == null}
        onChange={(e) => setTemplateName(e.target.value)}
        onBlur={() => onNameUpdate?.(templateName)}
      />
    </InputWrapper>,
  ].concat(
    castArray(breadcrumbItems).map((breadcrumbItem, idx) => {
      const { displayName, to } = breadcrumbItem;
      return idx === breadcrumbItems.length - 1 ? (
        // Last breadcrumb is secondary color and not clickable
        <BreadcrumbSecondary
          key={`breadcrumb-${idx}`}
          data-cy={`nav-breadcrumb__${displayName}`}
          className={className}
        >
          <Label>{displayName}</Label>
        </BreadcrumbSecondary>
      ) : (
        <BreadcrumbLink
          key={`breadcrumb-${idx}`}
          data-cy={`nav-breadcrumb__${displayName}`}
          className={className}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          to={to}
        >
          <Label>{displayName}</Label>
        </BreadcrumbLink>
      );
    }),
  );

  return (
    <Container className={className}>
      <BackButton
        id='nav-breadcrumb__initial'
        key='breadcrumb-back'
        aria-label='Back button'
        className={className}
        onClick={onBackClick}
      >
        {prefixElement}
      </BackButton>
      {intersperse(breadcrumbs, (i) => (
        <Separator key={`separator-${i}`}>{separator}</Separator>
      ))}
      {suffixElement}
    </Container>
  );
};

export default NavBreadcrumb;
